/**
 * Collection Cards v1.0.0
 * Gestion des interactions et animations pour les cards de collection
 */

class CollectionCards {
  constructor() {
    this.section = document.querySelector("zag-featured-collection");
    this.cards = this.section?.querySelectorAll(".collection-card");
    this.grid = this.section?.querySelector("zag-slider");

    this.init();
  }

  init() {
    if (!this.section) return;

    this.setupIntersectionObserver();
    this.setupKeyboardNavigation();
    this.setupAnalytics();
    this.setupSliderControls();
  }

  /**
   * Observer d'intersection pour les animations d'entrée
   */
  setupIntersectionObserver() {
    if (!this.cards?.length) return;

    const observerOptions = {
      threshold: 0.1,
      rootMargin: "0px 0px -50px 0px",
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("is-visible");
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    this.cards.forEach((card) => {
      observer.observe(card);
    });
  }

  /**
   * Navigation au clavier pour l'accessibilité
   */
  setupKeyboardNavigation() {
    if (!this.cards?.length) return;

    this.cards.forEach((card) => {
      card.addEventListener("keydown", (event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          card.click();
        }
      });

      // Améliorer le focus visible
      card.addEventListener("focus", () => {
        card.classList.add("is-focused");
      });

      card.addEventListener("blur", () => {
        card.classList.remove("is-focused");
      });
    });
  }

  /**
   * Analytics pour le tracking des clics
   */
  setupAnalytics() {
    if (!this.cards?.length) return;

    this.cards.forEach((card) => {
      card.addEventListener("click", (event) => {
        const collectionTitle = card
          .querySelector(".collection-title")
          ?.textContent?.trim();
        const collectionUrl = card.href;

        // Tracking personnalisé si nécessaire
        if (window.gtag) {
          gtag("event", "collection_card_click", {
            collection_title: collectionTitle,
            collection_url: collectionUrl,
          });
        }

        // Analytics Shopify si disponible
        if (window.ShopifyAnalytics) {
          window.ShopifyAnalytics.track("collection_card_click", {
            collection_title: collectionTitle,
            collection_url: collectionUrl,
          });
        }
      });
    });
  }

  /**
   * Configuration des boutons de navigation du slider
   */
  setupSliderControls() {
    // Trouver le slider dans cette section spécifique
    const slider = this.section.querySelector("zag-slider");
    const prevButton = this.section.querySelector(".slider-button--previous");
    const nextButton = this.section.querySelector(".slider-button--next");

    if (!slider || !prevButton || !nextButton) return;

    // Attendre que le slider soit initialisé
    const checkSliderReady = () => {
      if (slider.previous && slider.next) {
        this.connectSliderControls(slider, prevButton, nextButton);
      } else {
        setTimeout(checkSliderReady, 100);
      }
    };

    checkSliderReady();
  }

  connectSliderControls(slider, prevButton, nextButton) {
    // Gestion des clics
    prevButton.addEventListener("click", (e) => {
      e.preventDefault();
      slider.previous();
    });

    nextButton.addEventListener("click", (e) => {
      e.preventDefault();
      slider.next();
    });

    const updateButtonStates = () => {
      if (!slider.loop) {
        prevButton.disabled = slider.nearInlineStart;
        nextButton.disabled = slider.nearInlineEnd;
      }
    };

    slider.addEventListener("zag-slider:change", updateButtonStates);
    slider.addEventListener("zag-slider:edge-nearing", updateButtonStates);
    slider.addEventListener("zag-slider:edge-leaving", updateButtonStates);

    updateButtonStates();
  }

  /**
   * Méthode pour rafraîchir les cards (utile pour les mises à jour dynamiques)
   */
  refresh() {
    this.cards = this.section?.querySelectorAll(".collection-card");
    this.setupIntersectionObserver();
    this.setupKeyboardNavigation();
    this.setupAnalytics();
  }

  /**
   * Méthode pour obtenir les statistiques d'utilisation
   */
  getStats() {
    return {
      totalCards: this.cards?.length || 0,
      visibleCards:
        this.section?.querySelectorAll(".collection-card.is-visible")?.length ||
        0,
      sectionVisible: this.section?.classList.contains("is-visible") || false,
    };
  }
}

// Initialisation automatique
document.addEventListener("DOMContentLoaded", () => {
  new CollectionCards();
});

// Support pour les mises à jour dynamiques (Shopify Sections)
document.addEventListener("shopify:section:load", (event) => {
  if (event.target.querySelector("zag-featured-collection")) {
    new CollectionCards();
  }
});

// Export pour utilisation externe
window.CollectionCards = CollectionCards;
